import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';

export const EQ_DIMENSIONS = [
  { key: 'equipos', label: 'Equipos' },
  { key: 'eventos', label: 'Eventos' },
  { key: 'lugares', label: 'Lugares' },
];

export const EQ_DEFAULT_DIMENSIONS = ['equipos'];
const MIN_LENGTH = 2;
const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

export default function useEquipmentSearch({ query, dimensions }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [firstUpcomingDate, setFirstUpcomingDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const trimmed = (query || '').trim();
  const active = trimmed.length >= MIN_LENGTH && dimensions.length > 0;
  const dimsKey = dimensions.join(',');
  const seq = useRef(0);

  const fetchPage = useCallback(async (offset) => {
    const mine = ++seq.current;
    const params = new URLSearchParams({
      q: trimmed, in: dimsKey, limit: String(PAGE_SIZE), offset: String(offset),
    });
    const { data } = await api.get(`/checklists/planning/search?${params.toString()}`);
    return { data, stale: mine !== seq.current };
  }, [trimmed, dimsKey]);

  useEffect(() => {
    if (!active) {
      seq.current++;
      setEvents([]); setTotal(0); setFirstUpcomingDate(null); setLoading(false); setError(null);
      return;
    }
    setLoading(true); setError(null);
    const timer = setTimeout(() => {
      fetchPage(0)
        .then(({ data, stale }) => {
          if (stale) return;
          setEvents(data.events || []);
          setTotal(data.total || 0);
          setFirstUpcomingDate(data.firstUpcomingDate || null);
          setLoading(false);
        })
        .catch(err => {
          setError(err.response?.data?.error || 'No se pudo buscar');
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [active, fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingMore || events.length >= total) return;
    setLoadingMore(true);
    fetchPage(events.length)
      .then(({ data, stale }) => {
        if (!stale) {
          setEvents(prev => [...prev, ...(data.events || [])]);
          setTotal(data.total || 0);
        }
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  }, [fetchPage, loadingMore, events.length, total]);

  return {
    active, events, total, firstUpcomingDate,
    loading, loadingMore, error, loadMore,
    hasMore: events.length < total,
  };
}
